import React from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { makeStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import Divider from '@material-ui/core/Divider'

import settings from 'settings'
import selectors from 'store/selectors'
import routerActions from 'store/modules/router'
import networkActions from 'store/modules/network'
import resourceActions from 'store/modules/resource'


const FolderIcon = settings.icons.folder

const {
  resourceOrder,
  resources,
} = settings

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
  },
  sidebar: {
    minWidth: '300px',
    flexGrow: 0,
    position: 'relative',
  },
  paper: {
    width: '100%',
    height: '100%',
    borderRight: '1px solid #ccc',
  },
  list: {
    position: 'relative',
    height: '100%',
    overflowY: 'auto',
  },
  listItem: {
    paddingLeft: '10px',
    paddingTop: '5px',
    paddingBottom: '5px',
  },
  sectionListItem: {
    paddingTop: theme.spacing(),
    paddingBottom: theme.spacing(),
    marginBottom: theme.spacing(),
  },
  sectionDivider: {
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
  listIcon: {
    marginRight: '0px',
    minWidth: '30px',
    '& > svg': {
      fontSize: '22px',
    },
  },
  listIconCurrent: {
    '& > svg': {
      color: theme.palette.primary.main,
    },
  },
  listText: {
    paddingLeft: theme.spacing(),
    '& > span': {
      fontSize: '0.95em',
    },
  },
  listTextBold: {
    '& > span': {
      fontWeight: 'bold',
    },
  },
  listTextCurrent: {
    '& > span': {
      color: theme.palette.primary.main,
    },
  },
  content: ({disableTreeScroll}) => ({
    flexGrow: 1,
    width: '100%',
    height: '100%',
    overflowY: disableTreeScroll ? 'hidden' : 'auto',
  }),
}))


const ResourceTree = ({
  children,
}) => {

  
  const dispatch = useDispatch()

  const {
    section,
    parentid,
  } = useSelector(selectors.router.params)

  const route = useSelector(selectors.router.fullRoute)
  const items = useSelector(selectors.resource.treeCollection.entities)
  const tree = useSelector(selectors.resource.tree)

  const classes = useStyles({
    disableTreeScroll: route.disableTreeScroll ? true : false,
  })

  const onView = (section, parentid) => dispatch(routerActions.navigateTo('resource.list', { section, parentid }))

  const getListItem = ({
    id,
    title,
    Icon = FolderIcon,
    depth = 0,
    location,
    className,
  }) => {
  
    const isCurrent = section == location.section && parentid == location.parentid
    return (
      <ListItem 
        button 
        key={ id }
        className={ `${classes.listItem} ${className || ''}` }
        onClick={ () => onView(location.section, location.parentid) }
      >
        <div style={{
          marginLeft: depth * 14 + 'px',
        }} />
        <ListItemIcon className={ `${classes.listIcon} ${isCurrent ? classes.listIconCurrent : '' }` }>
          <Icon />
        </ListItemIcon>
        <ListItemText 
          className={ `${classes.listText} ${depth == 0 || isCurrent ? classes.listTextBold : ''} ${isCurrent ? classes.listTextCurrent : ''}` }
          primary={ title }
        />
      </ListItem>
    )
  }


  const renderItem = ({
    item,
    depth = 1,
  }) => {
    const results = [getListItem({
      id: item.id,
      title: item.name,
      depth,
      location: {
        section: item.section,
        parentid: item.id,
      }
    })]

    const childIds = tree.children[item.id] || []
    return results.concat(childIds.map(childId => {
      const child = items[childId]
      return renderItem({
        item: child,
        depth: depth + 1,
      })
    }))
  }


  return (
    <div className={ classes.root }>
      <div className={ classes.sidebar }>
        <Paper
          className={ classes.paper }
          square
          elevation={ 0 }
        >
          <div className={ classes.list }>
            <List component="nav">
              {
                resourceOrder.map((name, i) => {
                  const resource = resources[name]
                  const rootIds = tree.sections[name] || []
                  
                  const sectionItem = getListItem({
                    id: `section:${name}`,
                    title: resource.title,
                    Icon: resource.icon,
                    index: i,
                    location: {
                      section: name,
                      parentid: 'root',
                    },
                    className: classes.sectionListItem,
                  })

                  const childItems = section == name ? 
                    rootIds.reduce((all, itemId) => {
                      const item = items[itemId]
                      return all.concat(renderItem({
                        item,
                      }))
                    }, []) :
                    []

                  return [sectionItem]
                    .concat(childItems)
                    .concat([
                      <Divider 
                        key={ `${i}-divider` } 
                        className={ classes.sectionDivider }
                      />
                    ])
                })
              }
            </List>
          </div>
        </Paper>
      </div>
      <div className={ classes.content }>
        { children }
      </div>
    </div>
  )

}

export default ResourceTree

